import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { apiErrorCode, apiErrorMessage } from "@/api/client";
import { getCompanies } from "@/api/endpoints";
import { RecordListPage } from "@/components/record-list-page";
import { Badge, Button, Card, Input } from "@/components/ui/primitives";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import { companySearchText } from "@/lib/search-index";
import { created, push } from "@/lib/sync";
import type { Company } from "@/types";

const WRITABLE_PLANS = ["premium", "grace"];

export function CompaniesPage() {
  const { t } = useI18n();
  const { activeOrgId, activeOrg, role } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canWrite = role === "admin" && WRITABLE_PLANS.includes(activeOrg?.plan ?? "free");

  const create = useMutation({
    mutationFn: async (companyName: string) => {
      // Every field the record will have, set explicitly. The push is a
      // `$set`, so anything left out simply never exists — and a company with
      // no `searchText` is one nobody finds again.
      const record = created({
        name: companyName,
        notes: "",
        rating: null,
        riskOptionId: null,
        fieldIds: [],
        riskTagIds: [],
        isIndividual: false,
        searchText: companySearchText(
          { name: companyName, notes: "" },
          { locations: [], channels: [], persons: [], notes: [] }
        ),
      });
      await push({ companies: [record] });
      return record._id;
    },
    onSuccess: (id) => {
      setName("");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["companies", activeOrgId] });
      void queryClient.invalidateQueries({ queryKey: ["overview", activeOrgId] });
      // Straight into the new company: a name on its own is not a record
      // anybody wanted, it is the first field of one.
      navigate(`/companies/${id}`);
    },
    onError: (caught) => {
      const code = apiErrorCode(caught);
      setError(
        code === "PLAN_REQUIRED"
          ? t("planRequiredWrite")
          : code === "FORBIDDEN"
            ? t("onlyAdmins")
            : apiErrorMessage(caught, t("networkError"))
      );
    },
  });

  return (
    <div className="space-y-3">
      {canWrite && (
        <Card className="flex flex-wrap items-center gap-2 p-3">
          <Input
            value={name}
            placeholder={t("newCompanyName")}
            className="max-w-xs"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && name.trim() !== "") create.mutate(name.trim());
            }}
          />
          <Button
            size="sm"
            disabled={create.isPending || name.trim() === ""}
            onClick={() => create.mutate(name.trim())}
          >
            <Plus className="size-4" />
            {t("newCompany")}
          </Button>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </Card>
      )}

      <RecordListPage<Company>
        title={t("navCompanies")}
        resource="companies"
        fetcher={getCompanies}
        renderRow={(company) => (
          <Link to={`/companies/${company._id}`} className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{company.name}</p>
              {company.notes && (
                <p className="truncate text-sm text-muted-foreground">{company.notes}</p>
              )}
            </div>
            {company.rating != null && <Badge tone="primary">{company.rating} ★</Badge>}
          </Link>
        )}
      />
    </div>
  );
}
